import React, { useState } from 'react';
import axios from 'axios';
import { trackAdsConversion, ADS_CONVERSION_LEAD } from '../mock';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { MapPin, Clock, Send } from 'lucide-react';
import { GOOGLE_MAPS_URL, CONTACT_ADDRESS, CONTACT_HOURS } from '../mock';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactSection = ({ defaultMotivo = '' }) => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    nombre: '',
    telefono: '',
    motivo: defaultMotivo,
    mensaje: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/contact`, formData);
      if (response.data && response.data.id) {
        trackAdsConversion(ADS_CONVERSION_LEAD);
        toast({
          title: '¡Consulta enviada!',
          description: 'Nos pondremos en contacto a la brevedad.',
        });
        setFormData({ nombre: '', telefono: '', motivo: defaultMotivo, mensaje: '' });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast({
        title: 'Error',
        description: 'Hubo un problema al enviar tu consulta. Intentá nuevamente.',
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="contact-section" id="contacto">
      <div className="contact-container">
        <h2 className="contact-title">Da el primer paso hacia tu tranquilidad.</h2>

        <div className="contact-info-block" data-testid="contact-info-block">
          <div className="contact-info-item">
            <MapPin className="contact-info-icon" strokeWidth={1.8} />
            <div>
              <p className="contact-info-label">Dirección</p>
              <p className="contact-info-value">{CONTACT_ADDRESS}</p>
            </div>
          </div>
          <div className="contact-info-item">
            <Clock className="contact-info-icon" strokeWidth={1.8} />
            <div>
              <p className="contact-info-label">Horario de atención</p>
              <p className="contact-info-value">{CONTACT_HOURS}</p>
            </div>
          </div>
        </div>

        <div className="contact-address">
          <a 
            href={GOOGLE_MAPS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="address-link"
            data-testid="google-maps-link"
          >
            📍 Ver cómo llegar en Google Maps
          </a>
        </div>
        
        <div className="contact-content">
          <form onSubmit={handleSubmit} className="contact-form">
            <div className="form-group">
              <Label htmlFor="nombre" className="form-label">Nombre</Label>
              <Input
                id="nombre"
                name="nombre"
                type="text"
                value={formData.nombre}
                onChange={handleChange}
                required
                className="form-input"
                placeholder="Tu nombre completo"
              />
            </div>

            <div className="form-group">
              <Label htmlFor="telefono" className="form-label">Teléfono</Label>
              <Input
                id="telefono"
                name="telefono"
                type="tel"
                value={formData.telefono}
                onChange={handleChange}
                required
                className="form-input"
                placeholder="+54 9 11 XXXX-XXXX"
              />
            </div>

            <div className="form-group">
              <Label htmlFor="motivo" className="form-label">Motivo de consulta</Label>
              <Input
                id="motivo"
                name="motivo"
                type="text"
                value={formData.motivo}
                onChange={handleChange}
                required
                className="form-input"
                placeholder="Ej: Divorcio, Sucesión, Despido, etc."
              />
            </div>

            <div className="form-group">
              <Label htmlFor="mensaje" className="form-label">Mensaje</Label>
              <Textarea
                id="mensaje"
                name="mensaje"
                value={formData.mensaje}
                onChange={handleChange}
                required
                className="form-textarea"
                placeholder="Contános brevemente tu situación..."
                rows={5}
              />
            </div>

            <Button
              type="submit"
              disabled={isSubmitting}
              className="form-submit-btn"
              size="lg"
            >
              <Send className="w-5 h-5 mr-2" />
              {isSubmitting ? 'Enviando...' : 'Enviar Consulta'}
            </Button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
